import type { NavItem, NavLink, NavTeaser } from '@sprd/spreadshirt-shell';
import type { ReactNode } from 'react';
import type { AmplienceImage, AmplienceLinkType } from '@/types/amplience';
import type {
  HeaderContent,
  HeaderTeaser,
  LinkLevel1,
  LinkLevel2,
  LinkLevel3,
  MutatedHeaderTeaserSlot,
} from '@/types/headerHierarchy';
import type { HeaderContentPro, LinkLevel1Pro } from '@/types/spreadshirtProHeader';
import {
  getB2BorSpreadshirtLink,
  isCYOItem,
  isProHeaderContent,
} from '@/utility/headerNavigationHelpers';

/** The app-specific rendering the shell's nav tree cannot know about. */
export type NavRenderers = {
  image: (image: AmplienceImage) => ReactNode;
  teaser: (teaser: HeaderTeaser) => ReactNode;
};

export type ExternalNavItem = NavItem & { link: NavLink };

export type SocialLinkData = {
  key: string;
  link: NavLink;
  label: string;
  icon: string | undefined;
};

/** `rel` is left unset: the shell unions `noopener noreferrer` in for a `_blank` target. */
export const toNavLink = (link: AmplienceLinkType): NavLink => ({
  href: link.url,
  target: link.openInNewTab ? '_blank' : undefined,
  'data-tracking': link.tracking || undefined,
});

export const toNavTeaser = (
  slot: MutatedHeaderTeaserSlot | undefined,
  render: NavRenderers['teaser']
): NavTeaser | undefined => {
  const teaser = slot?.headerTeaser;
  if (!teaser) return undefined;

  return { headline: teaser.headline || undefined, content: render(teaser) };
};

const toColumns = (level3: LinkLevel3[]): NavItem[][] =>
  level3.map((column) =>
    column.content.column.map(({ content }) => ({
      key: content._meta.deliveryId,
      label: content.link.label,
      link: toNavLink(content.link),
    }))
  );

const toLevel2 = (item: LinkLevel2, renderers: NavRenderers): NavItem => {
  const key = item.content._meta.deliveryId;

  if (isCYOItem(item)) {
    const { link, headline, items } = item.content;
    return {
      key,
      kind: 'cyo',
      label: link.label,
      link: toNavLink(link),
      headline,
      children: items.map((shortcut, index) => ({
        key: `${key}-${index}`,
        label: shortcut.link.label,
        subLabel: shortcut.link.subLabel,
        link: toNavLink(shortcut.link),
        image: renderers.image(shortcut.image),
      })),
    };
  }

  const { link, level3, headerTeaserSlot } = item.content;
  return {
    key,
    label: link.label,
    link: link.isLinkEnabled ? toNavLink(link) : undefined,
    columns: toColumns(level3),
    teaser: toNavTeaser(headerTeaserSlot, renderers.teaser),
  };
};

const toLevel1 = (item: LinkLevel1, renderers: NavRenderers): NavItem => {
  const { _meta, link, level2, headerTeaserSlot } = item.content;
  return {
    key: _meta.deliveryId,
    label: link.label,
    subLabel: link.subLabel,
    link: link.url ? toNavLink({ ...link, url: link.url }) : undefined,
    children: level2.map((level2Item) => toLevel2(level2Item, renderers)),
    teaser: toNavTeaser(headerTeaserSlot, renderers.teaser),
  };
};

/** Pro has no level2: a level1 entry opens its columns directly. */
const toLevel1Pro = (item: LinkLevel1Pro, renderers: NavRenderers): NavItem => {
  const { _meta, link, level2, headerTeaserSlot } = item.content;
  return {
    key: _meta.deliveryId,
    label: link.label,
    subLabel: link.subLabel,
    link: toNavLink(link),
    columns: toColumns(level2),
    teaser: toNavTeaser(headerTeaserSlot, renderers.teaser),
  };
};

export const toNavItems = (
  content: HeaderContent | HeaderContentPro,
  renderers: NavRenderers
): NavItem[] =>
  isProHeaderContent(content)
    ? content.content.level1.map((item) => toLevel1Pro(item, renderers))
    : content.content.level1.map((item) => toLevel1(item, renderers));

/** The B2B (or, on Pro, the Spreadshirt) link and "start selling", enabled ones only. */
export const toExternalLinks = (content: HeaderContent | HeaderContentPro): ExternalNavItem[] =>
  [
    { key: 'primary', link: getB2BorSpreadshirtLink(content) },
    { key: 'startSelling', link: content.content.startSellingLink },
  ].flatMap(({ key, link }) =>
    link.isLinkEnabled
      ? [{ key, label: link.label, subLabel: link.subLabel, link: toNavLink(link) }]
      : []
  );

/** Both lists are typed as required, but the Pro hierarchy ships the header without them. */
export const toFooterLinks = (content: HeaderContent | HeaderContentPro): NavItem[] =>
  (content.content.menuFooter ?? []).map(({ link }, index) => ({
    key: `${link.label}-${index}`,
    label: link.label,
    link: toNavLink(link),
  }));

export const toSocialLinks = (content: HeaderContent | HeaderContentPro): SocialLinkData[] =>
  (content.content.menuSocials?.iconLinks ?? []).map(({ link }, index) => ({
    key: `${link.label}-${index}`,
    link: toNavLink(link),
    label: link.label,
    icon: link.icon,
  }));
